import useLayoutConfigSetting from 'src/hooks/useLayoutConfigSetting';
import { GridConfigValue } from 'src/hooks/useTradePageLayout/type';
import { ORDERBOOK_DISPLAY_TYPE } from './useOrderbookDisplayPreference';
import { formatText } from '@velocity-exchange/common/format';
import { PRESETS, withDecimals } from 'src/utils/formatNumber/presets';
import { BigNum, PRICE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { Z_INDEX } from 'src/constants/zIndex';
import {
	CategorisedLiquidity,
	CUMULATIVE_SIZE_CURRENCY,
} from '@velocity-exchange/common';

const getHoverSideToUse = (
	displayPreference: GridConfigValue
): 'left' | 'right' => {
	switch (displayPreference) {
		case 'mirrored':
			return 'right';
		case 'split':
			return 'right';
		case 'default':
		case 'splitMirrored':
		case 'defaultTablet':
		default: {
			return 'left';
		}
	}
};

/**
 * Renders the extra content of the orderbook row when hovering over it
 *
 * NOTE FOR REASONING OF THIS COMPONENT:
 *
 * This has been implemented to work using CSS rather than javascript, will explain why below:
 *
 * - Previously we tried implementing this using Tippy, which was really bad for performance. With some cursory googling people suggest using Windowing / Virtualization when using Tippy on a large list of elements using Tippy.
 * - We have also tried using Virtualizating/Windowing but it created its own issues .. mainly : the windowing screwed with the "recenter" button's ability to work correctly. Possibly that first attempt was just done wrong, but it was a huge lift to get it working and I couldn't figure out how to get the recenter working after that.
 * - Also note that looking at backpack exchange's orderbook (which is the gold standard imo) with react devtools, their orderbook component is re-rendering an insane amount. I think they are getting away with it because the rest of their web app looks super optimised.
 * - Using javascript events to capture the hover state is really hard (I couldn't figure it out) because the onMouseEnter/onMouseLeave events can easily get skipped when the user moves their mouse around quickly - so a tooltip can stay even when they've moved their mouse away. The only way I can think to avoid this would be by adding a further mouse event catcher to the PARENT orderbook class - but I can't think of a way to do that without adding a lot of performance overhead (e.g. each event would make all rows re-render, or every render of every row would require setting up a subscribing to the parent's mouse events as well as the row's own events)
 * - Related to the above ^ :: the key consideration here was trying to make sure that _every row_ didn't need to re-render when the hover state changes for _any row_.
 * - To achieve reliable mouse events, and not add the performance overhead of capturing events, using css to only display the content when _it is actually being hovered_ (css can catch hovering 100% reliably) I decided to make the tooltip content get triggered with css instead of js.
 * - The performance cost of this solution is that for each row element being rendered, the tooltip content is also being rendered regardless of hover state, BUT browsers are apparently highly optimised when rendering things with "display:none" (which is what we are using) so I believe it is actually very little performance overhead.
 * - It was necessary to use the `fixed` display strategy with top/left positioning because there is an ancestor component with `overflow:auto` which prevents this content from being able to display outside of the main orderbook box otherwise. I couldn't find a way to make it work without removing that overflow - which would break the scrolling of the orderbook.
 *
 * @param param0
 * @returns
 */
export const OrderbookRowHoverContent = ({
	cumulativeAvgPrice,
	cumulativeTotalSize,
	cumulativeLiquidity,
	side,
	displayType,
	otherSideWrapperBoundingClientRect,
	rowsSectionWrapperBoundingClientRect,
	baseDisplayDecimals,
	currency,
}: {
	cumulativeAvgPrice: number;
	cumulativeTotalSize: number;
	cumulativeLiquidity: CategorisedLiquidity;
	side: 'buy' | 'sell';
	displayType: ORDERBOOK_DISPLAY_TYPE;
	otherSideWrapperBoundingClientRect: DOMRect | null;
	rowsSectionWrapperBoundingClientRect: DOMRect | null;
	baseDisplayDecimals: number;
	currency: CUMULATIVE_SIZE_CURRENCY;
}) => {
	const [displayPreference] = useLayoutConfigSetting();

	// Get market-specific decimal precision (must be called before any early returns)
	const currentMarket = useVelocityStore((s) => s.selectedMarket.current);
	const marketSymbol = currentMarket?.uiSymbols.baseAssetSymbol ?? '';
	const priceDecimals = getMarketPriceDecimals(marketSymbol);

	if (!rowsSectionWrapperBoundingClientRect) return;

	const HOVER_CONTENT_HEIGHT = 120; // Approximate height of hover content
	const MIN_TOP_POSITIONING = 48 + 10; // 48 is the height of the top nav bar component

	// Calculate position to center hover content on the specific side
	let topPosition: number;

	if (displayType === ORDERBOOK_DISPLAY_TYPE.VERTICAL) {
		// For vertical layout: center within the specific side (buy or sell)
		if (side === 'sell' && otherSideWrapperBoundingClientRect) {
			// For sell side: otherSideWrapperBoundingClientRect is the buy side
			// We want to center within the sell section (above the buy side)
			const sellSectionBottom = otherSideWrapperBoundingClientRect.top;
			const sellSectionHeight =
				sellSectionBottom - rowsSectionWrapperBoundingClientRect.top;
			topPosition =
				rowsSectionWrapperBoundingClientRect.top +
				sellSectionHeight / 2 -
				HOVER_CONTENT_HEIGHT / 2;
		} else if (side === 'buy' && otherSideWrapperBoundingClientRect) {
			// For buy side: otherSideWrapperBoundingClientRect is the sell side
			// We want to center within the buy section (below the sell side)
			const buySectionTop = otherSideWrapperBoundingClientRect.bottom;
			const buySectionHeight =
				rowsSectionWrapperBoundingClientRect.bottom - buySectionTop;
			topPosition =
				buySectionTop + buySectionHeight / 2 - HOVER_CONTENT_HEIGHT / 2;
		} else {
			// Fallback: center in entire orderbook if individual side rects unavailable
			topPosition =
				rowsSectionWrapperBoundingClientRect.top +
				rowsSectionWrapperBoundingClientRect.height / 2 -
				HOVER_CONTENT_HEIGHT / 2;
		}
	} else {
		// For horizontal layout: center in the middle of the entire orderbook
		topPosition =
			rowsSectionWrapperBoundingClientRect.top +
			rowsSectionWrapperBoundingClientRect.height / 2 -
			HOVER_CONTENT_HEIGHT / 2;
	}

	// Ensure minimum top positioning to avoid nav bar overlap
	topPosition = Math.max(MIN_TOP_POSITIONING, topPosition);

	const hoverSideToUse = getHoverSideToUse(displayPreference ?? 'default');

	// Calculate resting (orderbook) vs indicative (JIT) liquidity
	const indicativeSize = cumulativeLiquidity.indicative ?? 0;
	const restingOrderbookSize = Math.max(
		0,
		cumulativeTotalSize - indicativeSize
	);

	const displayDecimals =
		currency === CUMULATIVE_SIZE_CURRENCY.USD ? 2 : baseDisplayDecimals;

	const toDisplayValue = (value: number) =>
		currency === CUMULATIVE_SIZE_CURRENCY.USD && cumulativeAvgPrice
			? value * cumulativeAvgPrice
			: value;

	const formatSizeWithDenomination = (value: number) => {
		const formatted = formatText(toDisplayValue(value), {
			digits: {
				kind: 'decimals',
				decimals: displayDecimals,
				rounding: 'half-ceil',
			},
		});
		return currency === CUMULATIVE_SIZE_CURRENCY.USD
			? `$${formatted}`
			: `${formatted} ${marketSymbol}`;
	};

	return (
		<div
			className="pointer-events-none fixed hidden text-text-default group-focus-within:flex group-hover:flex"
			style={{
				top: `${topPosition}px`,
				left:
					hoverSideToUse === 'left'
						? `${rowsSectionWrapperBoundingClientRect.left - 10}px`
						: `${rowsSectionWrapperBoundingClientRect.right + 10}px`,
				transform:
					hoverSideToUse === 'left' ? 'translateX(-100%)' : 'translateX(0)',
				zIndex: Z_INDEX.tooltip,
			}}
		>
			<div className="inline-flex max-w-80 flex-col items-start justify-start gap-y-md rounded-sm bg-tooltip-bg p-md">
				<div className="flex flex-col gap-y-lg typo-label-sm">
					<div className="inline-flex w-56 items-start justify-between typo-label-xs">
						<span>Price (USD)</span>
						<span
							className={
								side === 'buy'
									? 'text-text-positive-green-button'
									: 'text-text-negative-red-button'
							}
						>
							{formatText(
								BigNum.fromPrint(
									cumulativeAvgPrice.toString(),
									PRICE_PRECISION_EXP
								),
								withDecimals(PRESETS.usdTruncate, priceDecimals)
							)}
						</span>
					</div>
					<div className="inline-flex items-start justify-between self-stretch">
						<span>Cumulative Size</span>
						<span>{formatSizeWithDenomination(cumulativeTotalSize)}</span>
					</div>
				</div>
				<div className="flex w-full flex-col gap-y-xs">
					<div className="inline-flex items-start justify-between self-stretch typo-body-xs">
						<span>Orderbook</span>
						<span>{formatSizeWithDenomination(restingOrderbookSize)}</span>
					</div>
					{indicativeSize > 0 && (
						<div className="inline-flex items-start justify-between self-stretch typo-body-xs">
							<span>Additional Liquidity (JIT)</span>
							<span>{formatSizeWithDenomination(indicativeSize)}</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
